import { IconArrowSquareOut, IconVideo } from "@btnet/blueprint-icons";
import { BTLink } from "@btnet/core-components/BTLink";
import { BTStatusBadge } from "@btnet/core-components/BTStatusBadge";
import { BTText } from "@btnet/core-components/BTText";
import type { WhatsNewItemResponse } from "@btnet/services-btnet/handlers/models/whatsNewItemResponse";
import { cn } from "@btnet/utilities-core";

import { DateDisplayDateOrRecentLabel } from "commonComponents/utilities/DateDisplay/DateDisplayDate";

import { defaultCtaLabel, defaultVideoLabel, isInternalUrl } from "entity/whatsNew/whatsNew.utils";

interface IWhatsNewCardProps {
    className?: string;
    item: WhatsNewItemResponse;
    isUnread: boolean;
    onCtaClicked: (item: WhatsNewItemResponse) => void;
    onVideoClicked: (item: WhatsNewItemResponse) => void;
}

export function WhatsNewCard({
    className,
    item,
    isUnread,
    onCtaClicked,
    onVideoClicked,
}: Readonly<IWhatsNewCardProps>) {
    const { ctaUrl, videoUrl } = item;

    return (
        <div
            className={cn("WhatsNewCard flex flex-col gap-2.5", className)}
            data-testid="whatsNewCard"
        >
            {/* Top-aligned, so pills that wrap leave the dot and date on the first row. Each sits
                in an h-8 slot: one row of pills plus the pill row's py-1. */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-2">
                    {/* Kept when read, so the text below doesn't shift. */}
                    <div className="flex h-8 w-2.5 shrink-0 items-center">
                        {isUnread && (
                            <>
                                <div
                                    className="size-2.5 rounded-full bg-blue-70"
                                    data-testid="whatsNewCardUnreadDot"
                                />
                                <span className="sr-only">New</span>
                            </>
                        )}
                    </div>
                    <div
                        className={cn("flex min-w-0 flex-wrap items-center gap-1 py-1", {
                            // With no dot, the pill's padding sits in the empty dot column, so
                            // its letters line up with the title.
                            "-ml-2": !isUnread,
                        })}
                    >
                        {item.tags.map((tag) => (
                            // Bounds the flex item so an over-wide tag ends in an ellipsis. The
                            // badge's tooltip sets a pointer cursor, but tags aren't clickable.
                            <div
                                key={tag.id}
                                className="min-w-0 max-w-full [&_.BTPopover]:cursor-default"
                            >
                                <BTStatusBadge
                                    testid="whatsNewCardTag"
                                    text={tag.name}
                                    displayType="default"
                                    textOnly
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <BTText
                    size="normal-sm"
                    className="flex h-8 shrink-0 items-center text-fg-secondary"
                >
                    <DateDisplayDateOrRecentLabel
                        value={item.publishedDateUtc}
                        showTime={false}
                        showDayOfWeek={false}
                        data-testid="whatsNewCardPublishedDate"
                    />
                </BTText>
            </div>
            <div className="flex flex-col gap-2 pl-4.5">
                <div className="flex flex-col">
                    <BTText
                        // Bold, like notification titles.
                        className="font-bold"
                        testid="whatsNewCardTitle"
                        text={item.title}
                        size="distinct-sm"
                    />
                    <BTText
                        // Plain text, so keep the author's line breaks.
                        className="whitespace-pre-wrap"
                        testid="whatsNewCardBody"
                        text={item.body}
                        size="normal-md"
                    />
                </div>
                {(ctaUrl || videoUrl) && (
                    // Wraps when two long labels don't fit on one line.
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {ctaUrl && (
                            <BTLink
                                className="inline-flex items-center gap-1.5"
                                data-testid="whatsNewCardCta"
                                href={ctaUrl}
                                shouldOpenNewTab
                                onClick={() => {
                                    onCtaClicked(item);
                                }}
                            >
                                {!isInternalUrl(ctaUrl) && (
                                    // Icons default to near-black; match the link.
                                    <IconArrowSquareOut
                                        testid="whatsNewCardCtaExternalIcon"
                                        className="text-fg-link"
                                    />
                                )}
                                {item.ctaLabel || defaultCtaLabel}
                            </BTLink>
                        )}
                        {videoUrl && (
                            <BTLink
                                className="inline-flex items-center gap-1.5"
                                data-testid="whatsNewCardVideo"
                                href={videoUrl}
                                shouldOpenNewTab
                                onClick={() => {
                                    onVideoClicked(item);
                                }}
                            >
                                <IconVideo
                                    testid="whatsNewCardVideoIcon"
                                    className="text-fg-link"
                                />
                                {item.videoLabel || defaultVideoLabel}
                            </BTLink>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
